<template>
  <button
    type="button"
    class="mobile-agent-entry"
    :class="{ 'is-icon-only': iconOnly }"
    :aria-label="entryLabel"
    :title="entryLabel"
    :disabled="busy || !available || isSelf"
    :aria-busy="String(busy)"
    @click.stop="open"
  >
    <ChatNavigationIcon :name="scene === 'GENERAL' ? 'support' : 'agent'" />
    <span v-if="!iconOnly">{{ entryLabel }}</span>
  </button>
</template>
<script>
import { Toast } from "vant";
import ChatNavigationIcon from "./ChatNavigationIcon.vue";
import { openSupplyDemandConsultation } from "@/api/agentService";
import { resolveAgentServiceCurrentUserId } from "@/services/agent-service-state";

/** 手机业务入口适配，不提交订单、不发送占位消息；权限和接待人仍由原服务核验。 */
export default {
  name: "MobileAgentEntry",
  components: { ChatNavigationIcon },
  props: {
    iconOnly: Boolean,
    scene: {
      type: String,
      default: "GENERAL",
      validator: (v) =>
        ["GENERAL", "PRODUCT", "ORDER", "SUPPLY_DEMAND"].includes(v),
    },
    goodsCode: [String, Number],
    orderNum: [String, Number],
    clueId: [String, Number],
    publisherUserId: [String, Number],
    available: { type: Boolean, default: true },
  },
  data: () => ({ busy: false, epoch: 0 }),
  computed: {
    entryLabel() {
      return this.$t("chatWorkbench.agent." + this.scene.toLowerCase());
    },
    identity() {
      return (
        resolveAgentServiceCurrentUserId(this.$store.state.user || {}) +
        ":" +
        String(this.$store.state.user?.userInfo?.token || "")
      );
    },
    contextKey() {
      return [
        this.scene,
        this.goodsCode,
        this.orderNum,
        this.clueId,
        this.publisherUserId,
      ].join(":");
    },
    isSelf() {
      return (
        this.scene === "SUPPLY_DEMAND" &&
        Boolean(this.publisherUserId) &&
        String(this.publisherUserId) ===
          String(resolveAgentServiceCurrentUserId(this.$store.state.user || {}))
      );
    },
  },
  watch: {
    identity() {
      this.epoch++;
      this.busy = false;
    },
    contextKey() {
      this.epoch++;
      this.busy = false;
    },
    "$route.fullPath"() {
      this.epoch++;
      this.busy = false;
    },
  },
  beforeDestroy() {
    this.epoch++;
  },
  methods: {
    async open() {
      if (this.busy || !this.available || this.isSelf) return;
      if (
        !resolveAgentServiceCurrentUserId(this.$store.state.user || {}) ||
        !this.$store.state.user?.userInfo?.token
      ) {
        window.dispatchEvent(new Event("open-login-dialog"));
        return;
      }
      if (this.scene === "GENERAL") {
        this.$store.commit("chatWorkbench/open", { kind: "GENERAL" });
        return;
      }
      if (this.scene === "PRODUCT" || this.scene === "ORDER") {
        const field = this.scene === "PRODUCT" ? "goodsCode" : "orderNum";
        const value = String(this[field] || "").trim();
        if (value)
          this.$store.commit("chatWorkbench/open", {
            kind: "SCENE",
            sceneType: this.scene,
            [field]: value,
          });
        return;
      }
      if (!/^[1-9]\d*$/.test(String(this.clueId || ""))) return;
      const epoch = ++this.epoch,
        identity = this.identity,
        context = this.contextKey,
        path = this.$route.fullPath;
      const current = () =>
        epoch === this.epoch &&
        identity === this.identity &&
        context === this.contextKey &&
        path === this.$route.fullPath &&
        this.available &&
        !this.isSelf;
      this.busy = true;
      try {
        const entryId =
          globalThis.crypto?.randomUUID?.() ||
          `entry_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const response = await openSupplyDemandConsultation({
          clueId: String(this.clueId),
          entryId,
        });
        if (!current()) return;
        if (
          response.code !== 200 ||
          !response.data?.conversation?.conversationId ||
          !response.data?.context
        )
          throw new Error(
            response.msg || this.$t("pcHome.publisherConsultationFailed")
          );
        this.$store.commit("chatWorkbench/open", {
          kind: "SUPPLY_DEMAND",
          session: response.data,
        });
      } catch (error) {
        if (current())
          Toast.fail(
            error.message || this.$t("pcHome.publisherConsultationFailed")
          );
      } finally {
        if (epoch === this.epoch) this.busy = false;
      }
    },
  },
};
</script>
<style lang="less" scoped>
.mobile-agent-entry {
  &.is-icon-only {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    color: #334155;
    background: transparent;
    ::v-deep svg {
      stroke-width: 1.7;
    }
  }
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  min-width: 44px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #dde3ee;
  border-radius: 12px;
  background: #fff;
  color: #246de0;
  font: inherit;
  font-size: 14px;
  span {
    white-space: nowrap;
  }
  &:disabled {
    opacity: 0.5;
  }
  &:focus-visible {
    outline: 2px solid #4286f5;
    outline-offset: 2px;
  }
}
</style>
